import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { FormGroup, Input, Label, Spinner } from "reactstrap";
import { Dispatch } from "redux";
import { resourceAction } from "../../actions/resourceAction";
import {
	DABBackToList,
	DABButton,
	DABTextBox,
	LoadingSpinnerModal,
} from "../../components/dab";
import uploadFileToBlob from "../../services/Azure/azureBlob";
import { getLoggedUserId } from "../../services/selectors/Accounts";
import { showAlert, showAlertWithBacktrack } from "../common/functions";

class Guid {
	static newGuid() {
		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
			/[xy]/g,
			function (c) {
				var r = (Math.random() * 16) | 0,
					v = c == "x" ? r : (r & 0x3) | 0x8;
				return v.toString(16);
			}
		);
	}
}

const ALLOWED_IMAGE_FORMATS = ["png", "jpg", "jpeg"];
const ORDER_NUMBER_OPTIONS = [1, 2, 3, 4, 5];
// Only as many resources can be active as there are order numbers.
const ACTIVE_LIST_PAGE_SIZE = ORDER_NUMBER_OPTIONS.length;
const ACTION_BUTTON_TEXT_MAX_LENGTH = 12;

interface ResourceProps {
	dispatch: Dispatch<any>;
	resourceData: any;
	callback: any;
	id: number;
}

const ResourceDetail: React.SFC<ResourceProps> = ({
	dispatch,
	resourceData,
	callback,
	id,
}) => {
	const history = useHistory();

	const isEditMode = id > 0;

	const [resourceTitle, setResourceTitle] = useState("");
	const [actionButtonText, setActionButtonText] = useState("");
	const [resourceLinkUrl, setResourceLinkUrl] = useState("");
	const [orderNum, setOrderNum] = useState<any>(null);
	const [selectedImgFile, setImageFile] = useState<any>(null);
	const [selectedFileName, setSelectedFileName] = useState("");
	const [existingImageUrl, setExistingImageUrl] = useState("");
	const [isUploadingFile, setUploading] = useState(false);
	const [formSubmit, setFormSubmit] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [isDetailLoaded, setIsDetailLoaded] = useState<boolean>(false);

	// const takenOrderNumbers: number[] = React.useMemo(
	// 	() =>
	// 		(resourceData?.activeItems?.products ?? [])
	// 			.filter(
	// 				(resource: any) =>
	// 					!isEditMode || parseInt(resource?.productId) !== id
	// 			)
	// 			.map((resource: any) => parseInt(resource?.sortOrder))
	// 			.filter((sortOrder: number) => !isNaN(sortOrder)),
	// 	[resourceData?.activeItems, isEditMode, id]
	// );

	React.useEffect(() => {
		if (isEditMode) {
			setShowModal(true);
			dispatch(resourceAction.resourceGetDetail(id));
		}
	}, [id]);

	// React.useEffect(() => {
	// 	dispatch(
	// 		resourceAction.resourceGetActiveList({
	// 			sortDirection: "",
	// 			sortColumn: "",
	// 			pageNumber: 1,
	// 			pageSize: ACTIVE_LIST_PAGE_SIZE,
	// 			searchText: "",
	// 			isActive: true,
	// 			fromDate: null,
	// 			toDate: null,
	// 		})
	// 	);
	// }, []);

	React.useEffect(() => {
		if (
			isEditMode &&
			!isDetailLoaded &&
			resourceData?.isDetailComplete &&
			resourceData?.resourceInput
		) {
			const detail = resourceData.resourceInput?.product;
			setResourceTitle(detail?.productTitle ?? "");
			setActionButtonText(detail?.actionButtonText ?? "");
			setResourceLinkUrl(detail?.productLinkUrl ?? "");
			setOrderNum(detail?.sortOrder ?? null);
			setExistingImageUrl(detail?.productImageUrl ?? "");
			setSelectedFileName(detail?.productImageUrl?.split("/").pop() ?? "");
			setIsDetailLoaded(true);
			setShowModal(false);
			callback(detail?.productTitle ?? "");
		}
	}, [resourceData?.isDetailComplete]);

	if (resourceData) {
		if (resourceData.isError && !resourceData.isSuccess) {
			if (showModal) {
				setShowModal(false);
			}
			resourceData.isError = false;
			if (resourceData.status === "Unauthorized") {
				sessionStorage.setItem(
					"fromPath",
					window.location.pathname + window.location.search
				);
				history.push("/401");
			} else if (resourceData.isDetailError) {
				showAlertWithBacktrack(
					resourceData.status || "Unable to load the resource details.",
					true,
					history
				);
			} else {
				showAlert(resourceData.status, true);
			}
		}

		if (
			showModal &&
			formSubmit &&
			!resourceData.isFormSubmit &&
			resourceData.isSuccess
		) {
			setShowModal(false);
			window.localStorage.setItem("alertMessage", resourceData.status);
			window.location.href = "/resources";
		}
	}

	const handleImageFileChange = (event) => {
		const file = event.target.files[0];
		if (!file) return;

		const extension = file.name.split(".").pop().toLowerCase();
		if (ALLOWED_IMAGE_FORMATS.includes(extension)) {
			setImageFile(file);
			setSelectedFileName(file.name);
		} else {
			event.target.value = null;
			setImageFile(null);
			setSelectedFileName("");
			showAlert(
				`Please select a valid image file (${ALLOWED_IMAGE_FORMATS.join(
					", "
				)} only).`,
				true
			);
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!selectedImgFile && !existingImageUrl) {
			showAlert("Please select a resource image.", true);
			return;
		}

		try {
			let productImageUrl = existingImageUrl;
			if (selectedImgFile) {
				setUploading(true);
				productImageUrl = await uploadFileToBlob(
					selectedImgFile,
					Guid.newGuid(),
					"Image"
				);
				setUploading(false);
			}
			const data = {
				...(isEditMode ? { productId: id } : {}),
				productTitle: resourceTitle,
				actionButtonText: actionButtonText,
				sortOrder: orderNum ? parseInt(orderNum) : null,
				productImageUrl: productImageUrl,
				productLinkUrl: resourceLinkUrl,
				adminUserId: parseInt(getLoggedUserId()),
				isActive: true,
			};

			setShowModal(true);
			dispatch(resourceAction.resourceSave(id, data));
			setFormSubmit(true);
		} catch (error) {
			setUploading(false);
			showAlert("Image upload failed. Please try again.", true);
		}
	};

	const handleConfirmCancel = (e) => {
		e.preventDefault();
		e.stopPropagation();
		history.goBack();
	};

	return (
		<>
			<div className="product-detail mb-5">
				{showModal && (
					<LoadingSpinnerModal isShowModal={showModal} modalChange={() => { }} />
				)}
				<div className="card-box m-0">
					<DABBackToList
						onClick={(e) => {
							history.goBack();
						}}
					/>
					<form
						name="createform"
						onSubmit={handleSubmit}
						className="create-form"
					>
						<div className="row mb-3 flex-column">
							<div className="col-6">
								<FormGroup>
									<Label
										htmlFor="resourceTitle"
										data-testid="resourceTitle"
										className="col-form-label"
									>
										Resource Title
									</Label>
									<DABTextBox
										type="text"
										id="resourceTitle"
										data-testid="resourceTitleInput"
										isRequired={true}
										isHover={false}
										onChange={(event) => {
											setResourceTitle(event.target.value);
										}}
										value={resourceTitle}
										pattern=".*\S.*"
										title="Input cannot be only spaces"
										maxLength={40}
									/>
									{resourceTitle?.length >= 40 && (
										<span className="text-warning">
											Character limit met. Maximum 40 characters allowed
										</span>
									)}
								</FormGroup>
							</div>

							<div className="col-6">
								<FormGroup>
									<Label
										htmlFor="actionButtonText"
										data-testid="actionButtonText"
										className="col-form-label"
									>
										Action Button Text
									</Label>
									<DABTextBox
										type="text"
										id="actionButtonText"
										data-testid="actionButtonTextInput"
										isRequired={true}
										isHover={false}
										onChange={(event) => {
											setActionButtonText(event.target.value);
										}}
										value={actionButtonText}
										pattern=".*\S.*"
										title="Input cannot be only spaces"
										maxLength={ACTION_BUTTON_TEXT_MAX_LENGTH}
									/>
									{actionButtonText?.length >= ACTION_BUTTON_TEXT_MAX_LENGTH && (
										<span className="text-warning">
											Character limit met. Maximum {ACTION_BUTTON_TEXT_MAX_LENGTH}{" "}
											characters allowed
										</span>
									)}
								</FormGroup>
							</div>

							<div className="col-6">
								<FormGroup>
									<Label
										htmlFor="resourceLinkUrl"
										data-testid="resourceLinkUrl"
										className="col-form-label"
									>
										Resource Link URL
									</Label>
									<Input
										type="url"
										id="resourceLinkUrl"
										data-testid="resourceLinkUrlInput"
										required
										value={resourceLinkUrl}
										onChange={(e) => {
											setResourceLinkUrl(e.target.value);
										}}
										placeholder="https://example.com/resource"
										title="Please enter a valid URL (including https://)"
										maxLength={500}
									/>
								</FormGroup>
							</div>

							<div className="col-6">
								<FormGroup>
									<div className="d-flex flex-column mb-2">
										<Label
											htmlFor="orderno"
											className="col-form-label pb-0"
											data-testid="orderno"
										>
											Order No
										</Label>
										<span>
											(Note: If the selected order is in use, the system assigns the next available order to the existing resource. If none is available, the system swaps the order with the existing resource.)
										</span>
									</div>
									<Input
										type="select"
										id="orderno"
										data-testid="ordernoInput"
										value={orderNum ?? ""}
										onChange={(e) => {
											setOrderNum(e.target.value);
										}}
										required={true}
									>
										<option value="">Select Order No</option>
										{ORDER_NUMBER_OPTIONS.map((option) => (
											<option
												key={option}
												value={option}
												// disabled={takenOrderNumbers.includes(option)}
											>
												{option}
											</option>
										))}
									</Input>
								</FormGroup>
							</div>

							<div className="col-6">
								<FormGroup data-testid="imageFileContainer">
									<Label
										data-testid="lblImageFileCreate"
										htmlFor="imageFile"
										className="col-form-label"
									>
										Resource Image
									</Label>
									<Input
										type="file"
										name="imageFileUpload"
										id="imageFile"
										required={!selectedImgFile && !existingImageUrl}
										accept="image/png, image/jpeg, image/jpg"
										onChange={(e) => {
											handleImageFileChange(e);
										}}
									/>
								</FormGroup>
								<small className="text-muted d-block mb-1">
									Allowed file type: PNG, JPG, & JPEG only
								</small>
								{selectedFileName && (
									<div className="mb-1 text-muted">
										Selected file: <strong>{selectedFileName}</strong>
									</div>
								)}
							</div>
							<div className="col-6 ed-buttons d-flex justify-content-between align-items-center mt-2">
								<div data-testid="cancelButtonContainer">
									<DABButton
										id="backToCreate"
										className={`${isUploadingFile ? "disabled" : ""}`}
										onClick={handleConfirmCancel}
										value="CANCEL"
									>
										CANCEL
									</DABButton>
								</div>
								<div
									className="d-flex align-items-center gap-2"
									data-testid="createButtonContainer"
								>
									<DABButton
										id="submit"
										className={`btn-submit-frm ${isUploadingFile ? "disabled" : ""
											}`}
										value={isEditMode ? "UPDATE" : "CREATE"}
									>
										{isEditMode ? "Update" : "Create"}
									</DABButton>
									{isUploadingFile && (
										<>
											<span>Uploading...</span>
											<Spinner size="sm"></Spinner>
										</>
									)}
								</div>
							</div>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

const mapStateToProps = (state: any) => {
	const { resourceData } = state;
	return {
		resourceData,
	};
};

export default connect(mapStateToProps)(ResourceDetail);
